import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  ArrowLeft,
  Package,
  PackageCheck,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  MapPin,
  Hash,
  Save,
  Lock,
  Info,
  Building,
  Search,
  Filter,
  CheckSquare,
  Square,
  LayoutList,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Zap,
  Truck,
  ClipboardList,
  Printer,
  Barcode,
  ScanLine
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { getWarehouseLocations } from "@/services/location-stock-service";

// Types
interface SalesOrderLine {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityPicked: number;
  quantityOutstanding: number;
  unitPrice: number;
  lineTotal: number;
}

interface SalesOrder {
  id: string;
  soNumber: string;
  customerCode: string;
  customerName: string;
  customerContact: string;
  orderDate: Date;
  expectedDispatchDate: Date;
  status: string;
  totalValue: number;
  currency: string;
  deliveryAddress: {
    line1: string;
    line2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  lines: SalesOrderLine[];
}

interface PickingLine {
  soLineId: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityPreviouslyPicked: number;
  quantityOutstanding: number;
  quantityToPick: number;
  unitPrice: number;
  warehouseId: string;
  locationId: string;
  lotBatchSerial: string;
  isSelected: boolean;
  isFullyPicked: boolean;
  isPicked: boolean;
}

interface Warehouse {
  id: string;
  code: string;
  name: string;
}

interface Location {
  id: string;
  warehouseId: string;
  code: string;
  name: string;
}

// Mock data - Sales Orders
const MOCK_SALES_ORDERS: SalesOrder[] = [
  {
    id: "1",
    soNumber: "SO-2024-001",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    customerContact: "Jane Smith",
    orderDate: new Date("2024-01-15"),
    expectedDispatchDate: new Date("2024-02-15"),
    status: "partially-picked",
    totalValue: 15750.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 High Street",
      line2: "Unit 5",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    lines: [
      {
        id: "l1",
        lineNumber: 1,
        stockItemCode: "WGT-A",
        stockItemName: "Widget A",
        description: "Premium quality widget - Model A",
        quantityOrdered: 150,
        quantityPicked: 100,
        quantityOutstanding: 50,
        unitPrice: 45.00,
        lineTotal: 6750.00
      },
      {
        id: "l2",
        lineNumber: 2,
        stockItemCode: "PART-C",
        stockItemName: "Component C",
        description: "Standard component C - Pack of 10",
        quantityOrdered: 200,
        quantityPicked: 200,
        quantityOutstanding: 0,
        unitPrice: 22.50,
        lineTotal: 4500.00
      },
      {
        id: "l3",
        lineNumber: 3,
        stockItemCode: "RAW-X",
        stockItemName: "Raw Material X",
        description: "Raw material X - 25kg bags",
        quantityOrdered: 100,
        quantityPicked: 0,
        quantityOutstanding: 100,
        unitPrice: 45.00,
        lineTotal: 4500.00
      }
    ]
  },
  {
    id: "2",
    soNumber: "SO-2024-002",
    customerCode: "CUST-002",
    customerName: "XYZ Wholesale Corp",
    customerContact: "Tom Brown",
    orderDate: new Date("2024-01-20"),
    expectedDispatchDate: new Date("2024-01-25"),
    status: "ready-to-pick",
    totalValue: 8500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Industrial Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    lines: [
      {
        id: "l4",
        lineNumber: 1,
        stockItemCode: "WGT-B",
        stockItemName: "Widget B",
        description: "Widget B - Standard model",
        quantityOrdered: 100,
        quantityPicked: 0,
        quantityOutstanding: 100,
        unitPrice: 85.00,
        lineTotal: 8500.00
      }
    ]
  },
  {
    id: "3",
    soNumber: "SO-2024-003",
    customerCode: "CUST-003",
    customerName: "Global Trading Co",
    customerContact: "Lisa Chen",
    orderDate: new Date("2024-01-22"),
    expectedDispatchDate: new Date("2024-02-28"),
    status: "ready-to-pick",
    totalValue: 25600.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "789 Commerce Street",
      line2: "Building B",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    lines: [
      {
        id: "l5",
        lineNumber: 1,
        stockItemCode: "GDG-B",
        stockItemName: "Gadget B",
        description: "Gadget B - Professional series",
        quantityOrdered: 200,
        quantityPicked: 0,
        quantityOutstanding: 200,
        unitPrice: 128.00,
        lineTotal: 25600.00
      }
    ]
  }
];

// Mock data - Warehouses
const MOCK_WAREHOUSES: Warehouse[] = [
  { id: "wh1", code: "MAIN", name: "Main Warehouse" },
  { id: "wh2", code: "OVERFLOW", name: "Overflow Warehouse" },
  { id: "wh3", code: "RETURNS", name: "Returns Area" }
];

// Mock data - Locations
const MOCK_LOCATIONS: Location[] = [
  { id: "loc1", warehouseId: "wh1", code: "A1", name: "Aisle A, Bin 1" },
  { id: "loc2", warehouseId: "wh1", code: "A2", name: "Aisle A, Bin 2" },
  { id: "loc3", warehouseId: "wh1", code: "B1", name: "Aisle B, Bin 1" },
  { id: "loc4", warehouseId: "wh1", code: "C3", name: "Aisle C, Bin 3" },
  { id: "loc5", warehouseId: "wh2", code: "Z1", name: "Zone Z, Bin 1" },
  { id: "loc6", warehouseId: "wh3", code: "RET-1", name: "Returns Area 1" }
];

interface PickPackPageProps {
  pageParams?: {
    soId?: string;
    soNumber?: string;
    returnTo?: string;
  };
}

export function PickPackPage({ pageParams }: PickPackPageProps) {
  const { navigateTo } = useNavigation();
  
  // Determine if pre-loaded from SO page
  const isPreloaded = !!(pageParams?.soId || pageParams?.soNumber);
  const returnTo = pageParams?.returnTo;

  // State
  const [selectedSO, setSelectedSO] = useState<SalesOrder | null>(null);
  const [pickingDate, setPickingDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [pickListReference, setPickListReference] = useState<string>("");
  const [pickedBy, setPickedBy] = useState<string>("John Doe");
  const [notes, setNotes] = useState<string>("");
  const [pickingLines, setPickingLines] = useState<PickingLine[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  // New state for UX improvements
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showOutstandingOnly, setShowOutstandingOnly] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<"detailed" | "table">("detailed");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(50);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string>("");
  const [defaultLocationId, setDefaultLocationId] = useState<string>("");
  const [scanMode, setScanMode] = useState<boolean>(false);
  const [scanInput, setScanInput] = useState<string>("");

  // Initialize page
  useEffect(() => {
    if (isPreloaded) {
      // Load specific SO
      const soId = pageParams?.soId;
      const so = MOCK_SALES_ORDERS.find(s => s.id === soId);
      
      if (so) {
        setSelectedSO(so);
        setPickingLines(createPickingLines(so));
        setPickListReference(generatePickListReference());
      } else {
        toast.error("Sales order not found");
      }
    } else {
      // Generate pick list reference for new picking
      setPickListReference(generatePickListReference());
    }
  }, [isPreloaded, pageParams]);

  // Get available SOs for dropdown (only ready-to-pick/partially-picked)
  const availableSOs = useMemo(() => {
    return MOCK_SALES_ORDERS.filter(so => 
      so.status === "ready-to-pick" || so.status === "partially-picked"
    );
  }, []);

  // Create picking lines from SO
  const createPickingLines = (so: SalesOrder): PickingLine[] => {
    return so.lines.map(line => ({
      soLineId: line.id,
      lineNumber: line.lineNumber,
      stockItemCode: line.stockItemCode,
      stockItemName: line.stockItemName,
      description: line.description,
      quantityOrdered: line.quantityOrdered,
      quantityPreviouslyPicked: line.quantityPicked,
      quantityOutstanding: line.quantityOutstanding,
      quantityToPick: line.quantityOutstanding, // Pre-fill with outstanding
      unitPrice: line.unitPrice,
      warehouseId: mockWarehouses[0]?.code || "", // Default to first warehouse
      locationId: "",
      lotBatchSerial: "",
      isSelected: line.quantityOutstanding > 0, // Auto-select items with outstanding qty
      isFullyPicked: line.quantityOutstanding === 0,
      isPicked: false
    }));
  };

  // Generate pick list reference
  const generatePickListReference = (): string => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 9000) + 1000;
    return `PL-${year}-${random}`;
  };

  // Handle SO selection (only for non-preloaded mode)
  const handleSOSelection = (soId: string) => {
    if (isPreloaded) {
      toast.error("Cannot change SO when picking from Sales Orders page");
      return;
    }

    const so = MOCK_SALES_ORDERS.find(s => s.id === soId);
    if (so) {
      setSelectedSO(so);
      setPickingLines(createPickingLines(so));
    }
  };

  // Handle line selection toggle
  const handleLineToggle = (lineId: string) => {
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, isSelected: !line.isSelected }
          : line
      )
    );
  };

  // Handle quantity change
  const handleQuantityChange = (lineId: string, value: string) => {
    const quantity = parseInt(value) || 0;
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, quantityToPick: quantity }
          : line
      )
    );
  };

  // Handle warehouse change
  const handleWarehouseChange = (lineId: string, warehouseId: string) => {
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, warehouseId, locationId: "" } // Reset location when warehouse changes
          : line
      )
    );
  };

  // Handle location change
  const handleLocationChange = (lineId: string, locationId: string) => {
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, locationId }
          : line
      )
    );
  };

  // Handle lot/batch change
  const handleLotBatchChange = (lineId: string, value: string) => {
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, lotBatchSerial: value }
          : line
      )
    );
  };

  // Mark item as picked
  const handleMarkAsPicked = (lineId: string) => {
    setPickingLines(lines =>
      lines.map(line =>
        line.soLineId === lineId
          ? { ...line, isPicked: !line.isPicked }
          : line
      )
    );
  };

  // Get locations for warehouse
  const getLocationsForWarehouse = (warehouseId: string): Location[] => {
    const locations = getWarehouseLocations(warehouseId);
    return locations.map(loc => ({
      id: loc.locationCode,
      warehouseId: loc.warehouseCode,
      code: loc.locationCode,
      name: loc.locationCode
    }));
  };

  // Filter and search lines
  const filteredLines = useMemo(() => {
    let filtered = pickingLines;

    // Filter by outstanding only
    if (showOutstandingOnly) {
      filtered = filtered.filter(l => !l.isFullyPicked);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        l =>
          l.stockItemCode.toLowerCase().includes(query) ||
          l.stockItemName.toLowerCase().includes(query) ||
          l.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [pickingLines, showOutstandingOnly, searchQuery]);

  // Paginated lines
  const paginatedLines = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLines.slice(startIndex, endIndex);
  }, [filteredLines, currentPage, itemsPerPage]);

  // Total pages
  const totalPages = Math.ceil(filteredLines.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showOutstandingOnly]);

  // Bulk action handlers
  const handleSelectAllVisible = () => {
    const visibleIds = new Set(paginatedLines.map(l => l.soLineId));
    setPickingLines(lines =>
      lines.map(line =>
        visibleIds.has(line.soLineId) && !line.isFullyPicked
          ? { ...line, isSelected: true }
          : line
      )
    );
  };

  const handleDeselectAll = () => {
    setPickingLines(lines =>
      lines.map(line => ({ ...line, isSelected: false }))
    );
  };

  const handleFillQuantitiesToMax = () => {
    setPickingLines(lines =>
      lines.map(line =>
        line.isSelected && !line.isFullyPicked
          ? { ...line, quantityToPick: line.quantityOutstanding }
          : line
      )
    );
    toast.success("Quantities filled to maximum outstanding");
  };

  const handleApplyDefaults = () => {
    if (!defaultWarehouseId) {
      toast.error("Please select a default warehouse first");
      return;
    }

    setPickingLines(lines =>
      lines.map(line =>
        line.isSelected && !line.isFullyPicked
          ? {
              ...line,
              warehouseId: defaultWarehouseId,
              locationId: defaultLocationId || line.locationId
            }
          : line
      )
    );
    
    const message = defaultLocationId 
      ? "Warehouse and location applied to selected items"
      : "Warehouse applied to selected items";
    toast.success(message);
  };

  // Handle barcode scan
  const handleBarcodeScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scanInput.trim()) return;

    // Find matching item
    const matchedLine = pickingLines.find(
      line => line.stockItemCode.toLowerCase() === scanInput.toLowerCase()
    );

    if (matchedLine) {
      handleMarkAsPicked(matchedLine.soLineId);
      toast.success(`${matchedLine.stockItemName} marked as picked`);
      setScanInput("");
    } else {
      toast.error("Item not found in pick list");
    }
  };

  // Calculate summary
  const summary = useMemo(() => {
    const selectedLines = pickingLines.filter(l => l.isSelected && !l.isFullyPicked);
    const itemCount = selectedLines.length;
    const totalUnits = selectedLines.reduce((sum, line) => sum + line.quantityToPick, 0);
    const totalValue = selectedLines.reduce(
      (sum, line) => sum + (line.quantityToPick * line.unitPrice),
      0
    );
    const pickedCount = pickingLines.filter(l => l.isPicked).length;

    return { itemCount, totalUnits, totalValue, pickedCount };
  }, [pickingLines]);

  // Validation
  const validate = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!selectedSO) {
      errors.push("Please select a sales order");
      return { isValid: false, errors };
    }

    const selectedLines = pickingLines.filter(l => l.isSelected && !l.isFullyPicked);

    if (selectedLines.length === 0) {
      errors.push("Please select at least one item to pick");
    }

    selectedLines.forEach(line => {
      if (line.quantityToPick <= 0) {
        errors.push(`${line.stockItemName}: Quantity must be greater than 0`);
      }

      if (line.quantityToPick > line.quantityOutstanding) {
        errors.push(
          `${line.stockItemName}: Cannot pick more than outstanding (${line.quantityOutstanding})`
        );
      }

      if (!line.warehouseId) {
        errors.push(`${line.stockItemName}: Please select a warehouse`);
      }

      if (!line.locationId) {
        errors.push(`${line.stockItemName}: Please select a location`);
      }
    });

    if (!pickListReference.trim()) {
      errors.push("Please enter a pick list reference");
    }

    if (!pickingDate) {
      errors.push("Please enter a picking date");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Handle confirm pick
  const handleConfirmPick = () => {
    const validation = validate();

    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    setShowConfirmDialog(true);
  };

  // Handle submit
  const handleSubmit = async () => {
    setIsSaving(true);
    setShowConfirmDialog(false);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const linesToPick = pickingLines.filter(l => l.isSelected && !l.isFullyPicked);
      
      const pickingData = {
        soId: selectedSO!.id,
        soNumber: selectedSO!.soNumber,
        pickingDate,
        pickListReference,
        pickedBy,
        notes,
        lines: linesToPick.map(l => ({
          soLineId: l.soLineId,
          stockItemCode: l.stockItemCode,
          quantityPicked: l.quantityToPick,
          warehouseId: l.warehouseId,
          locationId: l.locationId,
          lotBatchSerial: l.lotBatchSerial || null
        }))
      };

      console.log("Submitting picking data:", pickingData);

      toast.success(`Items picked successfully! Pick List: ${pickListReference}`);

      // Navigate back or reset
      if (returnTo === "sales-orders") {
        setTimeout(() => {
          navigateTo("sales-orders");
        }, 1000);
      } else {
        // Reset form for next picking
        setSelectedSO(null);
        setPickingLines([]);
        setNotes("");
        setPickListReference(generatePickListReference());
        toast.info("Ready to pick from another SO");
      }
    } catch (error) {
      toast.error("Failed to record picking. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Pick & Pack</h1>
              {isPreloaded && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  <Lock className="h-3 w-3 mr-1" />
                  Pre-loaded
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              Pick items from warehouse locations for sales orders
            </p>
          </div>
          <div className="flex gap-2">
            {returnTo === "sales-orders" && (
              <Button
                variant="outline"
                onClick={() => navigateTo("sales-orders")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sales Orders
              </Button>
            )}
            <Button variant="outline" onClick={() => navigateTo("sales-orders")}>
              <FileText className="h-4 w-4 mr-2" />
              View All SOs
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {selectedSO && pickingLines.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StaticMetricCardAlt
            title="Items to Pick"
            value={summary.itemCount.toString()}
            change={`${summary.pickedCount} picked`}
            changeLabel="items marked"
            icon={Package}
            variant="info"
          />
          <StaticMetricCardAlt
            title="Total Units"
            value={summary.totalUnits.toString()}
            change={`${pickingLines.filter(l => !l.isFullyPicked).length}`}
            changeLabel="lines outstanding"
            icon={ClipboardList}
            variant="success"
          />
          <StaticMetricCardAlt
            title="Order Value"
            value={formatCurrency(summary.totalValue)}
            change={selectedSO.soNumber}
            changeLabel="sales order"
            icon={FileText}
            variant="warning"
          />
          <StaticMetricCardAlt
            title="Customer"
            value={selectedSO.customerName.split(' ')[0]}
            change={formatDate(selectedSO.expectedDispatchDate)}
            changeLabel="dispatch date"
            icon={Truck}
            variant="info"
          />
        </div>
      )}

      {/* SO Selection / Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isPreloaded ? (
              <>
                <Lock className="h-5 w-5 text-blue-600" />
                Sales Order Details
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                Select Sales Order
              </>
            )}
          </CardTitle>
          <CardDescription>
            {isPreloaded
              ? "This sales order is pre-selected and cannot be changed"
              : "Choose a sales order to pick items for"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPreloaded ? (
            // Pre-loaded SO details
            selectedSO && (
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">SO Number</Label>
                    <p className="font-mono font-bold text-lg">{selectedSO.soNumber}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Customer</Label>
                    <p className="font-medium">{selectedSO.customerName}</p>
                    <p className="text-sm text-muted-foreground">{selectedSO.customerCode}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Expected Dispatch</Label>
                    <p className="font-medium font-mono">{formatDate(selectedSO.expectedDispatchDate)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Outstanding Value</Label>
                    <p className="font-bold text-lg text-blue-600">
                      {formatCurrency(
                        selectedSO.lines.reduce((sum, line) => 
                          sum + (line.quantityOutstanding * line.unitPrice), 0
                        ),
                        selectedSO.currency
                      )}
                    </p>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground text-xs">Delivery Address</Label>
                  <p className="text-sm">
                    {selectedSO.deliveryAddress.line1}
                    {selectedSO.deliveryAddress.line2 && `, ${selectedSO.deliveryAddress.line2}`}
                    , {selectedSO.deliveryAddress.city}, {selectedSO.deliveryAddress.postcode}
                  </p>
                </div>
              </div>
            )
          ) : (
            // SO Selector
            <div className="space-y-4">
              <div>
                <Label>Sales Order</Label>
                <Select
                  value={selectedSO?.id || ""}
                  onValueChange={handleSOSelection}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a sales order..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSOs.map(so => {
                      const outstandingValue = so.lines.reduce(
                        (sum, line) => sum + (line.quantityOutstanding * line.unitPrice),
                        0
                      );
                      return (
                        <SelectItem key={so.id} value={so.id}>
                          <div className="flex items-center justify-between w-full">
                            <span className="font-mono font-medium">{so.soNumber}</span>
                            <span className="text-muted-foreground ml-4">{so.customerName}</span>
                            <span className="text-blue-600 font-medium ml-4">
                              {formatCurrency(outstandingValue, so.currency)} outstanding
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedSO && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Customer</Label>
                      <p className="font-medium">{selectedSO.customerName}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Order Date</Label>
                      <p className="font-mono">{formatDate(selectedSO.orderDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Expected Dispatch</Label>
                      <p className="font-mono">{formatDate(selectedSO.expectedDispatchDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Total Value</Label>
                      <p className="font-medium">{formatCurrency(selectedSO.totalValue, selectedSO.currency)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Picking Form */}
      {selectedSO && pickingLines.length > 0 && (
        <>
          {/* Picking Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Picking Details
              </CardTitle>
              <CardDescription>
                Enter picking information and pick list reference
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pickingDate">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Picking Date *
                  </Label>
                  <Input
                    id="pickingDate"
                    type="date"
                    value={pickingDate}
                    onChange={(e) => setPickingDate(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="pickListReference">
                    <Hash className="h-4 w-4 inline mr-2" />
                    Pick List Reference *
                  </Label>
                  <Input
                    id="pickListReference"
                    value={pickListReference}
                    onChange={(e) => setPickListReference(e.target.value)}
                    placeholder="PL-2024-0001"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="pickedBy">
                    <User className="h-4 w-4 inline mr-2" />
                    Picked By *
                  </Label>
                  <Select value={pickedBy} onValueChange={setPickedBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="John Doe">John Doe</SelectItem>
                      <SelectItem value="Jane Smith">Jane Smith</SelectItem>
                      <SelectItem value="Mike Wilson">Mike Wilson</SelectItem>
                      <SelectItem value="Sarah Johnson">Sarah Johnson</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="notes">
                    <FileText className="h-4 w-4 inline mr-2" />
                    Notes (Optional)
                  </Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barcode Scanning */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScanLine className="h-5 w-5" />
                Barcode Scanning
              </CardTitle>
              <CardDescription>
                Scan items to mark them as picked
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBarcodeScan} className="flex gap-2">
                <div className="flex-1">
                  <Input
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    placeholder="Scan or enter stock item code..."
                    autoFocus={scanMode}
                  />
                </div>
                <Button type="submit">
                  <Barcode className="h-4 w-4 mr-2" />
                  Scan
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Default Warehouse/Location (Optional) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-600" />
                Quick Setup (Optional)
              </CardTitle>
              <CardDescription>
                Set default warehouse and location to apply to all selected items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="defaultWarehouse">
                    <Building className="h-4 w-4 inline mr-2" />
                    Default Warehouse
                  </Label>
                  <Select
                    value={defaultWarehouseId}
                    onValueChange={(value) => {
                      setDefaultWarehouseId(value);
                      setDefaultLocationId(""); // Reset location when warehouse changes
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select warehouse..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mockWarehouses.map(wh => (
                        <SelectItem key={wh.code} value={wh.code}>
                          {wh.code} - {wh.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="defaultLocation">
                    <MapPin className="h-4 w-4 inline mr-2" />
                    Default Location
                  </Label>
                  <Select
                    value={defaultLocationId}
                    onValueChange={setDefaultLocationId}
                    disabled={!defaultWarehouseId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getLocationsForWarehouse(defaultWarehouseId).map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleApplyDefaults}
                    disabled={!defaultWarehouseId}
                    className="w-full"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Apply to Selected
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items to Pick */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Items to Pick
                  </CardTitle>
                  <CardDescription>
                    Select items and specify pick locations
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllVisible}
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Deselect All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFillQuantitiesToMax}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Fill to Max
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="showOutstanding"
                    checked={showOutstandingOnly}
                    onCheckedChange={(checked) => setShowOutstandingOnly(checked as boolean)}
                  />
                  <Label htmlFor="showOutstanding" className="cursor-pointer">
                    Show outstanding only
                  </Label>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Pick</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Ordered</TableHead>
                      <TableHead className="text-right">Picked</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead className="text-right">To Pick</TableHead>
                      <TableHead>Warehouse</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLines.map((line) => (
                      <TableRow key={line.soLineId} className={line.isPicked ? "bg-green-50" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={line.isSelected}
                            onCheckedChange={() => handleLineToggle(line.soLineId)}
                            disabled={line.isFullyPicked}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{line.stockItemName}</p>
                            <p className="text-sm text-muted-foreground">{line.stockItemCode}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{line.quantityOrdered}</TableCell>
                        <TableCell className="text-right">{line.quantityPreviouslyPicked}</TableCell>
                        <TableCell className="text-right font-medium">{line.quantityOutstanding}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            value={line.quantityToPick}
                            onChange={(e) => handleQuantityChange(line.soLineId, e.target.value)}
                            disabled={!line.isSelected || line.isFullyPicked}
                            className="w-20 text-right"
                            min="0"
                            max={line.quantityOutstanding}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={line.warehouseId}
                            onValueChange={(value) => handleWarehouseChange(line.soLineId, value)}
                            disabled={!line.isSelected || line.isFullyPicked}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {mockWarehouses.map(wh => (
                                <SelectItem key={wh.code} value={wh.code}>
                                  {wh.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={line.locationId}
                            onValueChange={(value) => handleLocationChange(line.soLineId, value)}
                            disabled={!line.isSelected || line.isFullyPicked || !line.warehouseId}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {getLocationsForWarehouse(line.warehouseId).map(loc => (
                                <SelectItem key={loc.id} value={loc.id}>
                                  {loc.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant={line.isPicked ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleMarkAsPicked(line.soLineId)}
                            disabled={!line.isSelected || line.isFullyPicked}
                          >
                            {line.isPicked ? (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Picked
                              </>
                            ) : (
                              <>
                                <Package className="h-4 w-4 mr-1" />
                                Mark
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredLines.length)} of{" "}
                    {filteredLines.length} items
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigateTo("sales-orders")}>
              Cancel
            </Button>
            <Button onClick={handleConfirmPick} disabled={isSaving}>
              <PackageCheck className="h-4 w-4 mr-2" />
              Confirm Pick
            </Button>
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Picking</DialogTitle>
            <DialogDescription>
              Are you sure you want to confirm this pick? This will update stock levels.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pick List:</span>
                <span className="font-mono font-medium">{pickListReference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items:</span>
                <span className="font-medium">{summary.itemCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Units:</span>
                <span className="font-medium">{summary.totalUnits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Value:</span>
                <span className="font-medium">{formatCurrency(summary.totalValue)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
